import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ProfileSectionPage from '../../../components/ProfileSectionPage/ProfileSectionPage';
import ProfileHeroMediaEditor from '../../../components/ProfileHeroMediaEditor';
import { Input } from '../../../components/common/Form';
import InfoTooltip from '../../../components/common/InfoTooltip/InfoTooltip';
import { useBrandContext } from '../../../contexts/BrandContext';
import { updateBrand } from '../../../api/brand';
import { getBrandMedia, saveBrandMedia } from '../../../api/brandMedia';
import { BrandMediaItem } from '../../../types/BrandMediaInterface';
import { BrandMediaListItem, listOrder, newFiles, removedIDs, toListItems } from '../../../utils/brandMedia';
import BrandProfileActions from './BrandProfileActions';
import './styles/_brand-cover-area.scss';

const MAX_COVER_MEDIA = 10;

const BrandCoverAreaPage = () => {
  const { currentBrand, refreshCurrentBrand } = useBrandContext();
  const [savedMedia, setSavedMedia] = useState<BrandMediaItem[] | null>(null);
  const [coverMedia, setCoverMedia] = useState<BrandMediaListItem[]>([]);
  const [primaryTagline, setPrimaryTagline] = useState('');
  const [secondaryTagline, setSecondaryTagline] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setPrimaryTagline(currentBrand?.primaryTagline || '');
    setSecondaryTagline(currentBrand?.secondaryTagline || '');
  }, [currentBrand]);

  useEffect(() => {
    if (!currentBrand) return;
    getBrandMedia(currentBrand.id)
      .then((media) => {
        setSavedMedia(media.profile);
        setCoverMedia(toListItems(media.profile));
      })
      .catch((error) => {
        console.error('Failed to load brand cover media:', error);
        toast.error('Failed to load cover page media.');
      });
  }, [currentBrand]);

  const handleSave = async () => {
    if (!currentBrand || !savedMedia || isSaving) return;

    setIsSaving(true);
    try {
      const media = await saveBrandMedia(currentBrand.id, {
        profile: newFiles(coverMedia),
        mediaToDelete: removedIDs(savedMedia, coverMedia),
        profileOrder: listOrder(coverMedia)
      });
      setSavedMedia(media.profile);
      setCoverMedia(toListItems(media.profile));

      await updateBrand(currentBrand.id, {
        primaryTagline: primaryTagline.trim() || null,
        secondaryTagline: secondaryTagline.trim() || null
      });
      await refreshCurrentBrand();
      toast.success('Cover area saved successfully.');
    } catch (error) {
      console.error('Failed to save brand cover area:', error);
      toast.error('Failed to save cover area.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ProfileSectionPage title="COVER AREA">
      <form className="location-profile-content" onSubmit={(e) => e.preventDefault()}>
        <div className="brand-cover-area-page">
          <ProfileHeroMediaEditor
            variant="uploader"
            label="RESTAURANT COVER PAGE MEDIA"
            description="Choose restaurant photos or videos for your cover page media. This media will be displayed as the primary media on your homepage. Each photo should be at least 100 px wide & 100 px tall and will be cropped to fit a 4:6 aspect ratio."
            media={coverMedia}
            onUpdate={setCoverMedia}
            maxItems={MAX_COVER_MEDIA}
            labelClassName="brand-cover-area-label"
            descriptionClassName="brand-cover-area-description"
          />

          <Input
            name="primaryTagline"
            label={
              <span className="flex items-center gap-1">
                PRIMARY TAGLINE <i>(Optional)</i>
                <InfoTooltip description="A short headline shown on your restaurant's profile page." />
              </span>
            }
            className="brand-cover-area-input"
            value={primaryTagline}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrimaryTagline(e.target.value)}
          />

          <Input
            name="secondaryTagline"
            label={
              <span className="flex items-center gap-1">
                SECONDARY TAGLINE <i>(Optional)</i>
                <InfoTooltip description="A supporting phrase that complements your primary tagline." />
              </span>
            }
            className="brand-cover-area-input"
            value={secondaryTagline}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSecondaryTagline(e.target.value)}
          />

          <BrandProfileActions onSave={handleSave} isSaving={isSaving} isDisabled={!savedMedia} />
        </div>
      </form>
    </ProfileSectionPage>
  );
};

export default BrandCoverAreaPage;
